import BaseModal from '@/components/ui/base-modal';
import { TSVG } from '@/types/svg.type';
import { X, Image, Download } from 'lucide-react';
import { motion } from 'motion/react';
import { useMemo } from 'react';
import { filePreview, isOpenFilePreviewModal, TMediaAttachment, useFilePreviewActions } from '@/stores/file.store';

const getFileTypeName = (mimeType: string) => {
  const typeMap: Record<string, string> = {
    'application/pdf': 'PDF Document',
    'application/msword': 'Word Document',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document',
    'application/vnd.ms-excel': 'Excel Spreadsheet',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel Spreadsheet',
    'application/vnd.ms-powerpoint': 'PowerPoint Presentation',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint Presentation',
    'application/zip': 'ZIP Archive',
    'application/x-rar-compressed': 'RAR Archive',
    'application/json': 'JSON File',
    'application/xml': 'XML File',
    'text/plain': 'Text File',
    'text/html': 'HTML File',
    'text/css': 'CSS File',
    'text/javascript': 'JavaScript File',
    'image/jpeg': 'JPEG Image',
    'image/png': 'PNG Image',
    'image/gif': 'GIF Image',
    'image/svg+xml': 'SVG Image',
    'video/mp4': 'MP4 Video',
    'video/avi': 'AVI Video',
    'audio/mp3': 'MP3 Audio',
    'audio/wav': 'WAV Audio',
  };

  return typeMap[mimeType] || mimeType;
};

const getTruncatedFileName = (fileName: string, maxLength: number = 25) => {
  if (fileName.length <= maxLength) {
    return fileName;
  }

  // Find the last dot to separate name and extension
  const lastDotIndex = fileName.lastIndexOf('.');

  if (lastDotIndex === -1) {
    // No extension found, show first part + ... + last part
    const startLength = Math.floor((maxLength - 3) / 2); // 3 = "...".length
    const endLength = maxLength - 3 - startLength;
    return fileName.substring(0, startLength) + '...' + fileName.substring(fileName.length - endLength);
  }

  const name = fileName.substring(0, lastDotIndex);
  const extension = fileName.substring(lastDotIndex);

  // Calculate available space for the name part (minus "..." and extension)
  const availableLength = maxLength - 3 - extension.length; // 3 = "...".length

  if (availableLength <= 6) {
    // If not enough space, just show ... with extension
    return '...' + extension;
  }

  // Split available length between start and end of name
  const startLength = Math.floor(availableLength / 2);
  const endLength = availableLength - startLength;

  return name.substring(0, startLength) + '...' + name.substring(name.length - endLength) + extension;
};

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getDisplayType = (mimeType: string) => {
  const friendlyName = getFileTypeName(mimeType);

  // If it's still a long MIME type, truncate it
  if (friendlyName === mimeType && friendlyName.length > 25) {
    return friendlyName.substring(0, 22) + '...';
  }

  return friendlyName;
};
const getExtension = (fileName: string): string => {
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex !== -1 ? fileName.substring(dotIndex + 1).toLowerCase() : '';
};

export const FILE_TYPES: Record<string, FileTypeConfig> = {
  // Images
  IMAGE: {
    extensions: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif', 'ico', 'heic', 'heif'],
    label: 'Image',
    bgColor: 'bg-[#E8F5E8]',
    textColor: 'text-[#2E7D32]',
    hoverBgColor: 'hover:bg-[#C8E6C9]',
    icon: '🖼️',
    type: 'image',
  },
  // Videos
  VIDEO: {
    extensions: ['mp4', 'mov', 'avi', 'wmv', 'flv', 'mkv', 'webm', 'm4v', '3gp', 'ogv', 'ts', 'mts'],
    label: 'Video',
    bgColor: 'bg-[#0C8DBF]',
    textColor: 'text-white',
    hoverBgColor: 'hover:bg-[#0a7ba6]',
    icon: '🎥',
    type: 'video',
  },
  // Audio
  AUDIO: {
    extensions: ['mp3', 'wav', 'flac', 'aac', 'ogg', 'wma', 'm4a', 'opus', 'amr', 'mid', 'midi'],
    label: 'Audio',
    bgColor: 'bg-[#9C27B0]',
    textColor: 'text-white',
    hoverBgColor: 'hover:bg-[#7B1FA2]',
    icon: '🎵',
    type: 'audio',
  },
  // Documents
  DOCUMENT: {
    extensions: ['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt', 'pages'],
    label: 'Document',
    bgColor: 'bg-[#FF5722]',
    textColor: 'text-white',
    hoverBgColor: 'hover:bg-[#E64A19]',
    icon: '📄',
    type: 'document',
  },
  // Spreadsheets
  SPREADSHEET: {
    extensions: ['xls', 'xlsx', 'csv', 'ods', 'numbers'],
    label: 'Spreadsheet',
    bgColor: 'bg-[#4CAF50]',
    textColor: 'text-white',
    hoverBgColor: 'hover:bg-[#388E3C]',
    icon: '📊',
    type: 'spreadsheet',
  },
  // Presentations
  PRESENTATION: {
    extensions: ['ppt', 'pptx', 'odp', 'key'],
    label: 'Presentation',
    bgColor: 'bg-[#FF9800]',
    textColor: 'text-white',
    hoverBgColor: 'hover:bg-[#F57C00]',
    icon: '📈',
    type: 'presentation',
  },
  // Archives
  ARCHIVE: {
    extensions: ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz', 'dmg', 'iso'],
    label: 'Archive',
    bgColor: 'bg-[#795548]',
    textColor: 'text-white',
    hoverBgColor: 'hover:bg-[#5D4037]',
    icon: '📦',
    type: 'archive',
  },
  // Code files
  CODE: {
    extensions: [
      'js',
      'ts',
      'jsx',
      'tsx',
      'html',
      'css',
      'scss',
      'sass',
      'json',
      'xml',
      'yaml',
      'yml',
      'md',
      'py',
      'java',
      'cpp',
      'c',
      'php',
      'rb',
      'go',
      'rs',
      'swift',
      'kt',
      'sql',
      'sh',
      'ps1',
      'bat',
    ],
    label: 'Code',
    bgColor: 'bg-[#607D8B]',
    textColor: 'text-white',
    hoverBgColor: 'hover:bg-[#455A64]',
    icon: '💻',
    type: 'code',
  },
  // CAD/3D files
  CAD: {
    extensions: ['dwg', 'dxf', 'stl', 'obj', 'fbx', '3ds', 'max', 'blend', 'skp'],
    label: 'CAD/3D',
    bgColor: 'bg-[#3F51B5]',
    textColor: 'text-white',
    hoverBgColor: 'hover:bg-[#303F9F]',
    icon: '🏗️',
    type: 'cad',
  },
  // Font files
  FONT: {
    extensions: ['ttf', 'otf', 'woff', 'woff2', 'eot'],
    label: 'Font',
    bgColor: 'bg-[#E91E63]',
    textColor: 'text-white',
    hoverBgColor: 'hover:bg-[#C2185B]',
    icon: '🔤',
    type: 'font',
  },
  // Database files
  DATABASE: {
    extensions: ['db', 'sqlite', 'mdb', 'accdb', 'fdb', 'odb'],
    label: 'Database',
    bgColor: 'bg-[#8BC34A]',
    textColor: 'text-white',
    hoverBgColor: 'hover:bg-[#689F38]',
    icon: '🗄️',
    type: 'database',
  },
  // Executable files
  EXECUTABLE: {
    extensions: ['exe', 'msi', 'dmg', 'pkg', 'deb', 'rpm', 'app', 'apk', 'ipa'],
    label: 'Executable',
    bgColor: 'bg-[#F44336]',
    textColor: 'text-white',
    hoverBgColor: 'hover:bg-[#D32F2F]',
    icon: '⚙️',
    type: 'executable',
  },
  // Unknown/Other
  UNKNOWN: {
    extensions: [],
    label: 'File',
    bgColor: 'bg-[#9E9E9E]',
    textColor: 'text-white',
    hoverBgColor: 'hover:bg-[#757575]',
    icon: '📎',
    type: 'unknown',
  },
};

export const getFileType = (extension: string): FileTypeConfig => {
  for (const [type, config] of Object.entries(FILE_TYPES)) {
    if (type === 'UNKNOWN') continue;
    if (config.extensions.includes(extension)) {
      return config;
    }
  }
  return FILE_TYPES.UNKNOWN;
};
interface FileTypeConfig {
  extensions: string[];
  label: string;
  bgColor: string;
  textColor: string;
  hoverBgColor: string;
  icon: string;
  type: string;
}

export default function FilePreviewModal() {
  const isOpenModal = isOpenFilePreviewModal();
  const { file, type } = filePreview();
  const { setModalFilePreview } = useFilePreviewActions();

  const displayPreview = useMemo(() => {
    if (!file) return null;

    // Handle URL string
    if (file && typeof file === 'string') {
      const fileName = file.split('/').pop() || 'Unknown file';
      const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(file);

      return (
        <motion.div className="bg-white rounded-[12px] shadow-2xl min-w-[500px] w-full max-h-[90vh] max-w-[600px] overflow-hidden">
          {/* Preview Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-[#FFF]">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                {isImage ? (
                  <Image className="w-6 h-6 text-[#0C8DBF]" />
                ) : (
                  <FileIcon className="w-6 h-6 text-[#0C8DBF]" />
                )}
              </div>
              <div className="flex flex-col">
                <span className="font-semibold text-[#0C8DBF] text-[16px]" title={fileName}>
                  {getTruncatedFileName(fileName)}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-[24px]">
              <button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = file;
                  link.download = fileName;
                  link.target = '_blank';
                  link.rel = 'noopener noreferrer';
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                }}
                className="px-[14px] py-[4px] text-[14px] font-normal text-white bg-[#1F445A] rounded-[8px] transition-colors cursor-pointer flex items-center gap-[6px]"
              >
                Download <Download className="w-[16px] h-[16px]" />
              </button>
              <button
                onClick={() => setModalFilePreview(false, { file: null, type: 'null' })}
                className="rounded-full p-[4px] overflow-hidden shrink-0 bg-[#F5F5F5] flex items-center justify-center cursor-pointer"
              >
                <X className="w-[16px] h-[16px] text-[#0C8DBF]" />
              </button>
            </div>
          </div>

          {/* Preview Content */}
          <div className="p-4 overflow-auto max-h-[calc(90vh-120px)]">
            {isImage ? (
              <div className="flex justify-center">
                <img src={file} alt={fileName} className="max-w-full max-h-full object-contain rounded-lg" />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="bg-[#E4F8FF] rounded-full flex items-center justify-center p-[16px]">
                  <FileIcon className="w-8 h-8 text-[#0C8DBF]" />
                </div>
                <h3 className="text-[14px] text-[#0C8DBF] mt-[8px]">File Preview</h3>
                <p className="text-[14px] font-normal text-[#9D9D9C] mt-[8px]">
                  Preview is not available. Please download the file.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      );
    } else if (file instanceof File) {
      return (
        <motion.div className="bg-white rounded-[12px] shadow-2xl min-w-[500px]  w-full max-h-[90vh] max-w-[600px] overflow-hidden">
          {/* Preview Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-[#FFF]">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                {file.type.startsWith('image/') ? (
                  <Image className="w-6 h-6 text-[#0C8DBF]" />
                ) : (
                  <FileIcon className="w-6 h-6 text-[#0C8DBF]" />
                )}
              </div>
              <div className="flex flex-col">
                <span className="font-semibold text-[#0C8DBF] text-[16px]" title={file.name}>
                  {getTruncatedFileName(file?.name || 'Unknown file name')}
                </span>
                <span
                  className="text-[12px] text-[#9D9D9C] truncate max-w-md"
                  title={
                    file.type
                      ? `${formatFileSize(file.size)} • ${file.type}`
                      : `${formatFileSize(file.size)} • Unknown type`
                  }
                >
                  {formatFileSize(file.size)} • {getDisplayType(file.type || 'Unknown type')}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-[24px]">
              {file.type.startsWith('image/') ? (
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = file.name;
                    link.download = file.name;
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  }}
                  className="px-[14px] py-[4px] text-[14px] font-normal text-white bg-[#1F445A] rounded-[8px] transition-colors cursor-pointer flex items-center gap-[6px]"
                >
                  Download <Download className="w-[16px] h-[16px]" />
                </button>
              ) : null}
              <button
                onClick={() => setModalFilePreview(false, { file: null, type: 'null' })}
                className="rounded-full p-[4px] overflow-hidden shrink-0 bg-[#F5F5F5] flex items-center justify-center cursor-pointer"
              >
                <X className="w-[16px] h-[16px] text-[#0C8DBF]" />
              </button>
            </div>
          </div>

          {/* Preview Content */}
          <div className="p-4 overflow-auto max-h-[calc(90vh-120px)]">
            {file.type.startsWith('image/') ? (
              <div className="flex justify-center">
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className="max-w-full max-h-full object-contain rounded-lg"
                  onLoad={(e) => {
                    // Clean up object URL after image loads
                    const img = e.target as HTMLImageElement;
                    setTimeout(() => {
                      if (img.src.startsWith('blob:')) {
                        URL.revokeObjectURL(img.src);
                      }
                    }, 1000);
                  }}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className=" bg-[#E4F8FF] rounded-full flex items-center justify-center p-[16px]">
                  <FileIcon className="w-8 h-8 text-[#0C8DBF]" />
                </div>
                <h3 className="text-[14px] text-[#0C8DBF] mt-[8px]">{getDisplayType(file.type || 'Unknown type')}</h3>
                <p className="text-[14px] font-normal text-[#9D9D9C] mt-[8px]">
                  Preview is not available in this view. Please download the file.
                </p>
                <button
                  // onClick={handleOpenPDF}
                  className="px-4 mt-[8px] text-[14px] font-semibold py-2 bg-[#1F445A] text-white rounded-[8px] hover:bg-[#1F445A]/90 transition-colors"
                >
                  Download File
                </button>
              </div>
            )}
          </div>
        </motion.div>
      );
    } else {
      const fileX = file as TMediaAttachment;

      const extension = getExtension(fileX.fileName);
      const fileType = getFileType(extension);

      const isImage = fileType.type === 'image';

      return (
        <motion.div className="bg-white rounded-[12px] shadow-2xl min-w-[500px]  w-full max-h-[90vh] max-w-[600px] overflow-hidden">
          {/* Preview Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-[#FFF]">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                {isImage ? (
                  <Image className="w-6 h-6 text-[#0C8DBF]" />
                ) : (
                  <FileIcon className="w-6 h-6 text-[#0C8DBF]" />
                )}
              </div>
              <div className="flex flex-col">
                <span className="font-semibold  text-[16px] text-[#0C8DBF]" title={fileX.fileName}>
                  {getTruncatedFileName(fileX.fileName || 'Unknown file name')}
                </span>
                <span
                  className="text-[12px] text-[#9D9D9C] truncate max-w-md"
                  title={
                    fileX.contentType
                      ? `${formatFileSize(fileX.size)} • ${fileX.contentType}`
                      : `${formatFileSize(fileX.size)} • Unknown type`
                  }
                >
                  {formatFileSize(fileX.size)} • {getDisplayType(fileX.contentType || 'Unknown type')}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-[24px]">
              {isImage ? (
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = fileX.uri;
                    link.download = fileX.fileName;
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  }}
                  className="px-[14px] py-[4px] text-[14px] font-normal text-white bg-[#1F445A] rounded-[8px] transition-colors cursor-pointer flex items-center gap-[6px]"
                >
                  Download <Download className="w-[16px] h-[16px]" />
                </button>
              ) : null}
              <button
                onClick={() => setModalFilePreview(false, { file: null, type: 'null' })}
                className="rounded-full p-[4px] overflow-hidden shrink-0 bg-[#F5F5F5] flex items-center justify-center cursor-pointer"
              >
                <X className="w-[16px] h-[16px] text-[#0C8DBF]" />
              </button>
            </div>
          </div>

          {/* Preview Content */}
          <div className="p-4 overflow-auto max-h-[calc(90vh-120px)]">
            {isImage ? (
              <div className="flex justify-center">
                <img
                  src={fileX.uri}
                  alt={fileX.fileName}
                  className="max-w-full max-h-full object-contain rounded-lg"
                  onLoad={(e) => {
                    // Clean up object URL after image loads
                    const img = e.target as HTMLImageElement;
                    setTimeout(() => {
                      if (img.src.startsWith('blob:')) {
                        URL.revokeObjectURL(img.src);
                      }
                    }, 1000);
                  }}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className=" bg-[#E4F8FF] rounded-full flex items-center justify-center p-[16px]">
                  <FileIcon className="w-8 h-8 text-[#0C8DBF]" />
                </div>
                <h3 className="text-[14px] text-[#0C8DBF] mt-[8px]">
                  {getDisplayType(fileX.fileName || 'Unknown type')}
                </h3>
                <p className="text-[14px] font-normal text-[#9D9D9C] mt-[8px]">
                  Preview is not available in this view. Please download the file.
                </p>
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = fileX.uri;
                    link.download = fileX.fileName;
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  }}
                  className="px-4 mt-[8px] text-[14px] font-semibold py-2 bg-[#1F445A] text-white rounded-[8px] hover:bg-[#1F445A]/90 transition-colors"
                >
                  Download File
                </button>
              </div>
            )}
          </div>
        </motion.div>
      );
    }

    // Fallback for unsupported file types
    return null;
  }, [file, type]);

  return (
    <BaseModal
      isCloseOutside={false}
      isButtonClose={false}
      onClose={() => {
        console.log('🔍 onClose');

        setModalFilePreview(false, { file: null, type: 'null' });
      }}
      className="  flex items-center justify-center p-0 h-max"
      isOpen={isOpenModal}
      title=""
    >
      {/* <h1>s</h1> */}
      {displayPreview}
    </BaseModal>
  );
}

export const FileIcon = (props: TSVG) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" {...props}>
      <path
        d="M19.9987 2.66699H7.9987C7.29145 2.66699 6.61318 2.94794 6.11308 3.44804C5.61298 3.94814 5.33203 4.62641 5.33203 5.33366V26.667C5.33203 27.3742 5.61298 28.0525 6.11308 28.5526C6.61318 29.0527 7.29145 29.3337 7.9987 29.3337H23.9987C24.7059 29.3337 25.3842 29.0527 25.8843 28.5526C26.3844 28.0525 26.6654 27.3742 26.6654 26.667V9.33366L19.9987 2.66699Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.668 2.66699V8.00033C18.668 8.70757 18.9489 9.38585 19.449 9.88594C19.9491 10.386 20.6274 10.667 21.3346 10.667H26.668"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
